"use client";

import React from 'react';
import ProductCard, { Product as CardProduct } from '../produit/product_card';

// --- Interfaces et Types ---
export interface Product {
  id: string;
  name: string;
  price: number;
  promoPrice?: number | null;
  images: string[];
  slug?: string;
  createdAt?: string;
  stock?: number;
  productFamily?: string;
  category?: {
    slug?: string;
    name?: string;
    parent?: string | ({ id?: string; name?: string }) | null;
  } | null;
}

// Types pour les filtres
type PriceFilter = 'all' | 'lt50' | '50to100' | 'gt100';
type StockFilter = 'all' | 'in' | 'out';
type OrderBy = 'default' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';

interface ProductGridProps {
  products: Product[];
  priceFilter?: PriceFilter;
  stockFilter?: StockFilter;
  orderBy?: OrderBy;
  // Couleur héritée de la catégorie parente (optionnelle)
  parentColorCataloge?: string | null;
}

export default function ProductGrid({ 
  products, 
  priceFilter = 'all',
  stockFilter = 'all',
  orderBy = 'default'
  , parentColorCataloge = null
}: ProductGridProps) {
  // Appliquer les filtres
  const filteredProducts = React.useMemo(() => {
    let filtered = products.filter(p => {
      const price = p.promoPrice != null && p.promoPrice < p.price ? p.promoPrice : p.price;
      
      // Filtre par prix
      let priceOk = true;
      if (priceFilter === 'lt50') priceOk = price < 50;
      else if (priceFilter === '50to100') priceOk = price >= 50 && price <= 100;
      else if (priceFilter === 'gt100') priceOk = price > 100;
      if (!priceOk) return false;

      // Filtre par stock
      if (stockFilter === 'in' && (!p.stock || p.stock === 0)) return false;
      if (stockFilter === 'out' && (p.stock && p.stock > 0)) return false;

      return true;
    });

    // Appliquer le tri
    if (orderBy === 'price-asc') {
      filtered = [...filtered].sort((a, b) => {
        const pa = a.promoPrice != null && a.promoPrice < a.price ? a.promoPrice : a.price;
        const pb = b.promoPrice != null && b.promoPrice < b.price ? b.promoPrice : b.price;
        return pa - pb;
      });
    } else if (orderBy === 'price-desc') {
      filtered = [...filtered].sort((a, b) => {
        const pa = a.promoPrice != null && a.promoPrice < a.price ? a.promoPrice : a.price;
        const pb = b.promoPrice != null && b.promoPrice < b.price ? b.promoPrice : b.price;
        return pb - pa;
      });
    } else if (orderBy === 'name-asc') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else if (orderBy === 'name-desc') {
      filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
    }

    return filtered;
  }, [products, priceFilter, stockFilter, orderBy]);

  // Adapter le produit pour ProductCard avec des types stricts
  const adaptProductForCard = (product: Product): CardProduct => {
    const finalPrice = product.promoPrice != null && product.promoPrice < product.price 
      ? product.promoPrice 
      : product.price;

    return {
      id: product.id,
      slug: product.slug || product.id,
      name: product.name,
      price: product.price,
      promoPrice: product.promoPrice != null && product.promoPrice < product.price ? product.promoPrice : undefined,
      images: product.images,
      stock: product.stock ?? 0,
      sku: product.id,
      category: {
        id: product.category?.slug || product.id,
        name: product.category?.name || 'Non catégorisé',
        // try to preserve category colors when available
        colorHome: (product.category as any)?.parent?.colorHome ?? (product.category as any)?.colorHome ?? undefined,
        colorCataloge: (product.category as any)?.parent?.colorCataloge ?? (product.category as any)?.colorCataloge ?? undefined,
      }
    };
  };

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Aucun produit disponible</p>
      </div>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Aucun produit ne correspond aux filtres sélectionnés</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
      {filteredProducts.map((product) => {
        const cardProduct = adaptProductForCard(product);

        return (
          <div key={product.id} className="w-full">
            <div className="mx-auto w-full max-w-[320px] sm:max-w-[280px] md:max-w-[260px] lg:max-w-none bg-white">
              <ProductCard product={cardProduct} />
            </div>
          </div>
        );
      })}
    </div>
  );
}